/**
 * Finance documents domain API (Phase 1b).
 *
 * Targets the org-scoped `/api/admin/finance/documents` endpoints for
 * the new "Belege" page. Distinct from the entity-scoped helpers in
 * `documents.js` (unit/tenant/landlord/property/owner documents) and
 * from the operational documents store in `operationalDocuments.js`.
 *
 * Endpoints (live since backend commit 87bd0b5, Migration 104):
 *   POST   /api/admin/finance/documents                   multipart create
 *   GET    /api/admin/finance/documents                   list with filters
 *   GET    /api/admin/finance/documents/{id}              detail
 *   PUT    /api/admin/finance/documents/{id}              partial update
 *   PATCH  /api/admin/finance/documents/{id}/status       status patch
 *   DELETE /api/admin/finance/documents/{id}              soft-delete (204)
 *   POST   /api/admin/finance/documents/{id}/file         file replace
 *   GET    /api/admin/finance/documents/{id}/file         signed URL
 *   DELETE /api/admin/finance/documents/{id}/file         file delete
 *
 * Currency lock: Phase 1 only accepts CHF. Other values are rejected
 * with HTTP 422 by the backend (see backend spec §6.2.1).
 *
 * Error handling: 3-stage pattern from core.parseAdminErrorResponse —
 * backend returns german detail messages which surface unchanged to
 * the user via toast.error(err?.message || fallback) in the page.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  getApiHeadersMultipart,
  parseAdminErrorResponse,
} from "./core";


/**
 * GET /api/admin/finance/documents
 *
 * @param {{
 *   type?: "expense" | "income" | "rent_payment",
 *   status?: "new" | "bexio_recorded",
 *   category?: string,
 *   date_from?: string,
 *   date_to?: string,
 *   unit_id?: string,
 *   paid_by_user_id?: string,
 *   q?: string,
 *   limit?: number,
 *   offset?: number,
 * }} [params]
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchAdminFinanceDocuments(params = {}) {
  const sp = new URLSearchParams();
  if (params.type) sp.set("type", params.type);
  if (params.status) sp.set("status", params.status);
  if (params.category) sp.set("category", params.category);
  if (params.date_from) sp.set("date_from", params.date_from);
  if (params.date_to) sp.set("date_to", params.date_to);
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  if (params.paid_by_user_id) sp.set("paid_by_user_id", params.paid_by_user_id);
  if (params.q) sp.set("q", params.q);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/finance/documents${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/finance/documents/{id}
 * Returns the full detail object including download_url (when has_file).
 */
export function fetchAdminFinanceDocument(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * POST /api/admin/finance/documents
 * Multipart create with optional file.
 *
 * @param {{
 *   type: "expense" | "income" | "rent_payment",
 *   amount_chf: number,
 *   category: string,
 *   document_date: string,
 *   currency?: "CHF",
 *   description?: string,
 *   unit_id?: string,
 *   tenant_id?: string,
 *   tenancy_id?: string,
 *   tenancy_revenue_id?: string,
 *   paid_by_user_id?: string,
 *   file?: File,
 * }} input
 * @returns {Promise<object>} FinanceDocumentDetailResponse
 */
export async function createAdminFinanceDocument(input) {
  const fd = new FormData();
  fd.append("type", String(input.type));
  fd.append("amount_chf", String(input.amount_chf));
  fd.append("category", String(input.category || "").trim());
  fd.append("document_date", String(input.document_date));
  fd.append("currency", input.currency || "CHF");

  const optionalStr = (k, v) => {
    if (v != null && String(v).trim() !== "") fd.append(k, String(v).trim());
  };
  optionalStr("description", input.description);
  optionalStr("unit_id", input.unit_id);
  optionalStr("tenant_id", input.tenant_id);
  optionalStr("tenancy_id", input.tenancy_id);
  optionalStr("tenancy_revenue_id", input.tenancy_revenue_id);
  optionalStr("paid_by_user_id", input.paid_by_user_id);

  if (input.file) fd.append("file", input.file, input.file.name);

  const res = await fetch(`${API_BASE_URL}/api/admin/finance/documents`, {
    method: "POST",
    headers: getApiHeadersMultipart(),
    body: fd,
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/**
 * PUT /api/admin/finance/documents/{id}
 * JSON partial-update body. Excludes type / status / file fields
 * (managed via PATCH /status and POST/DELETE /file endpoints).
 *
 * @param {string} documentId
 * @param {{
 *   amount_chf?: number,
 *   category?: string,
 *   document_date?: string,
 *   description?: string | null,
 *   unit_id?: string | null,
 *   tenant_id?: string | null,
 *   tenancy_id?: string | null,
 *   tenancy_revenue_id?: string | null,
 *   paid_by_user_id?: string | null,
 * }} payload
 */
export async function updateAdminFinanceDocument(documentId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}`,
    {
      method: "PUT",
      headers: getApiHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/**
 * PATCH /api/admin/finance/documents/{id}/status
 * Backend is idempotent for same-value patches (no audit row, no commit).
 *
 * @param {string} documentId
 * @param {"new" | "bexio_recorded"} status
 */
export async function patchAdminFinanceDocumentStatus(documentId, status) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}/status`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify({ status }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/**
 * DELETE /api/admin/finance/documents/{id}
 * Soft-delete — backend sets deleted_at. Returns 204 on success.
 * R2 object is retained (Schweizer OR Art. 958f: 10 Jahre).
 */
export async function deleteAdminFinanceDocument(documentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return { ok: true };
}


/**
 * POST /api/admin/finance/documents/{id}/file
 * Multipart replace of the receipt file (rollback-safe on backend).
 * Phase 1b: only the API helper exists; UI is Phase 1c.
 *
 * @param {string} documentId
 * @param {File} file
 */
export async function replaceAdminFinanceDocumentFile(documentId, file) {
  const fd = new FormData();
  fd.append("file", file, file.name);
  const res = await fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}/file`,
    {
      method: "POST",
      headers: getApiHeadersMultipart(),
      body: fd,
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/**
 * GET /api/admin/finance/documents/{id}/file
 * Returns a short-lived signed URL (15 min) for the R2 object.
 * Used by the Download-Button in row actions.
 *
 * @returns {Promise<{ url: string }>}
 */
export function fetchAdminFinanceDocumentFileUrl(documentId) {
  return fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}/file`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * DELETE /api/admin/finance/documents/{id}/file
 * R2-first delete (idempotent for NoSuchKey). Row stays alive with
 * file_* columns cleared. Phase 1b: only the API helper exists;
 * UI is Phase 1c.
 */
export async function deleteAdminFinanceDocumentFile(documentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/finance/documents/${encodeURIComponent(documentId)}/file`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}
